import { type ComponentPropsWithoutRef, type MouseEvent } from 'react';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { imageDetailModal } from '@/shared/modals/ImageDetailModal';
import { cn } from '@/utils';

/**
 * QuizImage - 퀴즈 카드용 이미지 컴포넌트
 * EditableQuizCard/QuizImage.tsx 및 EditableQuestionImage.tsx와 일치
 * @module QuizImage
 */

/**
 * QuestionImage - 메인 질문 이미지 래퍼 (EditableQuestionImage와 일치)
 * 적절한 크기 조정 및 클릭하여 확대 기능이 있는 퀴즈 질문 이미지에 사용
 */
const QuestionImage = ({
  src,
  alt = 'Question image',
  presentation
}: {
  src: string;
  alt?: string;
  presentation?: 'match-correct';
}) => {
  return <div className={cn('group relative flex h-[156px] w-full items-center justify-center rounded-sm border border-opacity-assistive bg-neutral p-sm mb-2xs', presentation === 'match-correct' && 'h-[174.667px] mb-0')} data-sentry-component="QuestionImage" data-sentry-source-file="QuizImage.tsx">
      <div className={cn('relative flex items-center justify-center', presentation === 'match-correct' && 'aspect-[257.37371826171875/140] h-full max-h-[150px]')}>
        <Container className={cn(presentation === 'match-correct' && 'rounded-sm border-0')} onClick={() => imageDetailModal.open(src)} data-sentry-element="Container" data-sentry-source-file="QuizImage.tsx">
          <Thumbnail className={cn('h-full max-h-[132px] w-full max-w-[578px] object-contain', presentation === 'match-correct' && 'size-full max-h-none max-w-none object-cover')} src={src} alt={alt} data-sentry-element="Thumbnail" data-sentry-source-file="QuizImage.tsx" />
        </Container>
      </div>
    </div>;
};
QuestionImage.displayName = 'QuizQuestionImage';
const Container = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    onClick,
    onKeyDown,
    children,
    ...rest
  } = props;
  const isClickable = onClick != null;
  return <div {...rest} role={isClickable ? 'button' : undefined} tabIndex={isClickable ? 0 : undefined} onClick={e => {
    onClick?.(e);
    e.stopPropagation();
  }} onKeyDown={e => {
    onKeyDown?.(e);
    if (!e.defaultPrevented && isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e as unknown as MouseEvent<HTMLDivElement>);
    }
  }} className={cn('relative rounded-xs border border-opacity group/image-thumbnail w-fit overflow-hidden flex items-center h-full cursor-pointer', className)} data-sentry-component="Container" data-sentry-source-file="QuizImage.tsx">
      <div className="absolute inset-0 bg-dimmed opacity-0 transition-opacity group-hover/image-thumbnail:opacity-100" />
      {children}
    </div>;
};
Container.displayName = 'QuizImageContainer';
const Thumbnail = (props: ComponentPropsWithoutRef<'img'>) => {
  const {
    className,
    src,
    ...rest
  } = props;
  return <img {...rest} className={cn('w-full h-full object-cover max-h-[132px] max-w-[558px]', className)} src={src} data-sentry-component="Thumbnail" data-sentry-source-file="QuizImage.tsx" />;
};
Thumbnail.displayName = 'QuizImageThumbnail';
const DetailThumbnail = ({
  src,
  alt = '',
  containerClassName,
  thumbnailClassName
}: {
  src: string;
  alt?: string;
  containerClassName?: string;
  thumbnailClassName?: string;
}) => <Container className={containerClassName} onClick={() => imageDetailModal.open(src)} data-sentry-element="Container" data-sentry-component="DetailThumbnail" data-sentry-source-file="QuizImage.tsx">
    <Thumbnail className={thumbnailClassName} src={src} alt={alt} data-sentry-element="Thumbnail" data-sentry-source-file="QuizImage.tsx" />
  </Container>;
DetailThumbnail.displayName = 'QuizImageDetailThumbnail';
const Loading = ({
  className
}: {
  className?: string;
}) => {
  return <div className={cn('flex h-full w-full max-w-[287px] items-center justify-center rounded-xs border border-opacity-assistive bg-dimmed', className)} data-sentry-component="Loading" data-sentry-source-file="QuizImage.tsx">
      <LoadingSpinner className="size-lg text-white" data-sentry-element="LoadingSpinner" data-sentry-source-file="QuizImage.tsx" />
    </div>;
};
Loading.displayName = 'QuizImageLoading';
export const QuizImage = {
  QuestionImage,
  Container,
  Thumbnail,
  DetailThumbnail,
  Loading
};